import React from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import PaymentFormErrorMessage from '@components/checkout/BillingPaymentFormErrorMessage';
import Colors from '@components/shared/Colors';
import { useEmailCapture } from '@utils/useEmailCapture';
import Button from './Button';
import Checkbox from './Checkbox';
import Spinner from './Spinner';
import SuccessMessage from './SuccessMessage';

interface Props {
  id: string;
  background?: string;
  textColor?: string;
}

const EmailCapture: React.FC<Props> = ({ id, background, textColor }) => {
  const { t } = useTranslation();
  const { email, setEmail, error, isChecked, setIsChecked, loading, successful, isValidEmail, handleAddToNewsletter } = useEmailCapture(id);

  return (
    <Container $background={background} $textColor={textColor}>
      <TextField
        placeholder={t('components.email-address-placeholder')}
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        style={{ opacity: successful ? 0.5 : 1.0 }}
        autoCapitalize="none"
        autoCorrect="none"
      />
      <CheckBox onClick={() => setIsChecked(!isChecked)}>
        <Checkbox
          checked={isChecked}
          style={{
            width: '25px',
            height: '25px',
            marginLeft: '10px',
            borderRadius: '2px'
          }}
        />
        <label>{t('components.receive-news')}</label>
      </CheckBox>
      <WaitlistButton
        $hoverColor={textColor}
        onClick={handleAddToNewsletter}
        disabled={!isValidEmail || loading || !isChecked || successful || error.length > 0}
      >
        {loading ? <Spinner /> : t('components.join-waitlist').toUpperCase()}
      </WaitlistButton>
      {(successful || error) && <div style={{ height: '20px' }} />}
      {successful && <SuccessMessage message={t('components.join-waitlist-success')} />}
      {error && <PaymentFormErrorMessage errorMessage={error} />}
    </Container>
  );
};

export default EmailCapture;

interface StyleProps {
  $background?: string;
  $textColor?: string;
}

interface ButtonProps {
  $hoverColor?: string;
}

const Container = styled.div<StyleProps>`
  background: ${({ $background }) => $background};
  color: ${({ $textColor }) => $textColor};
  width: 100%;
  display: flex;
  flex-direction: column;

  input {
    color: ${({ $textColor }) => $textColor};
  }

  & > * {
    width: 100%;
  }

  & > button {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
`;

const TextField = styled.input`
  background: ${Colors.cream};
  border: 1px solid ${Colors.gold};
  box-sizing: border-box;
  border-radius: 2px;
  padding-left: 15px;
  height: 56px;
  font-size: 16px;
  font-family: 'BrownStd';
  color: #003b1b;
  mix-blend-mode: normal;
  opacity: 0.5;
  width: 100%;
  border-radius: 0;

  &:focus {
    outline: none;
  }
`;

const CheckBox = styled.div`
  background: ${Colors.cream};
  cursor: pointer;
  height: 66px;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 10px;
  border-inline: 1px solid ${Colors.gold};

  label {
    cursor: pointer;
    font-family: BrownStd;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    text-align: left;
    color: inherit;
  }
`;

const WaitlistButton = styled(Button)<ButtonProps>`
  &:hover {
    background: ${({ $hoverColor }) => $hoverColor};
  }
`;
